// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import { hideAll, Instance as TippyInstance } from "tippy.js";
/* eslint-enable spire/fenced-imports */

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import Tippy, { TippyProps, TippyStyles } from "@west/App/Components/Tippy/Tippy";
import xHeaderUpdate from "@west/App/Handlers/XNG/Header";
import { CustomAppState } from "@west/App/Store/Reducers";
import { addScrollbarGutter, mergeWithCss, removeScrollbarGutter } from "@west/XDL/Utilities";
import React, { FC, useState } from "react";
import { useDispatch } from "react-redux";

export const sidebarMenuTippyWidth = 335;

interface SidebarMenuTippyStyles extends TippyStyles {}

export const sidebarMenuTippyStyles: SidebarMenuTippyStyles = {};

const SidebarMenuTippy: FC<TippyProps> = ({ children, extendedStyles, ...props }) => {
    const [styles] = useState(() => mergeWithCss(sidebarMenuTippyStyles, extendedStyles));
    const dispatch = useDispatch();
    const { headerHeight } = useAppSelector(state => ({
        headerHeight: parseInt((state as CustomAppState).xHeader.headerHeight, 10),
    }));

    const onShowHandler = (instance: TippyInstance) => {
        /**
         * Close all Tippy instances
         */
        hideAll();

        /**
         * Show overlay menu
         */
        dispatch(xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_popup` }));

        /**
         * Add overflow: hidden for body and add scrollbar-gutter for html.
         * we need it to prevent page scroll when tippy opened
         */
        addScrollbarGutter();
        ariaAppHider.hide(document.body);

        props.onShow?.(instance);
    };

    const onHideHandler = (instance: TippyInstance) => {
        /**
         * Hide overlay menu
         */
        dispatch(xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_popup` }));

        /**
         * Remove overflow: hidden from body and add scrollbar-gutter from html.
         * we need it to make page scrollable after tippy closed
         */
        removeScrollbarGutter();
        ariaAppHider.show(document.body);

        props.onHide?.(instance);
    };

    return (
        <Tippy
            {...props}
            onShow={onShowHandler}
            onHide={onHideHandler}
            extendedStyles={styles}
            appendTo={() => document.body}
            popperOptions={{
                strategy: "fixed",
                modifiers: [
                    {
                        name: "computeStyles",
                        enabled: true,
                        phase: "beforeWrite",
                        fn: ({ state }) => {
                            return {
                                ...state,
                                styles: {
                                    popper: {
                                        left: "initial",
                                        right: "0",
                                        bottom: "0",
                                        top: `${headerHeight}px`,
                                    },
                                },
                            };
                        },
                    },
                ],
            }}
        >
            {children}
        </Tippy>
    );
};

SidebarMenuTippy.defaultProps = {
    delay: 250,
    interactive: true,
    trigger: "click",
    offset: [0, 14],
    arrow: false,
    animation: "shift-away",
    placement: "left-end",
    maxWidth: sidebarMenuTippyWidth,
};

export default SidebarMenuTippy;
